import i18next from 'i18next'
import * as React from 'react'
import { InView } from 'react-intersection-observer'
import { Link } from 'widgets-for-react'
import { blockInviewHandler } from '../../../../utils/inview'
import { SVGIcon, ThinIconType } from '../../../../utils/svgicon'
import { camelize } from '../../../../utils/utils'
import { RootLayout } from '../../rootLayout'
import {
  KnowledgeType,
  getKnowledgeTypeSectionOfSlug,
  maybeGetKnowledgeType,
} from '../knowledgeUtils'
import { KnowledgeTypeTeasersState } from './knowledgeTypeTeasersState'

type KnowledgeTypeTeaserLayoutProps = {
  url: string
  title: string
  icon: ThinIconType
  intro: string
  count?: number
}

export const KnowledgeTypeTeaserLayout = (
  p: KnowledgeTypeTeaserLayoutProps
) => (
  <Link className="knowledge-teaser" to={p.url}>
    <article className="knowledge-teaser__inner">
      <header className="knowledge-teaser__header">
        <h2 className="knowledge-teaser__title title--xs">{p.title}</h2>
        <SVGIcon
          className="knowledge-teaser__title__icon"
          size={64}
          iconId={p.icon}
        />
      </header>

      <div className="knowledge-teaser__content">
        <p className="knowledge-teaser__intro">{p.intro}</p>
        <p className="knowledge-teaser__meta text--s">
            {p.count}{' '}
            {i18next.t(p.count === 1 ? 'knowledge:article' : 'knowledge:article_plural', {count: p.count})?.toLowerCase()}
        </p>
      </div>
    </article>
  </Link>
)

type KnowledgeTypeTeasersLayoutProps = {
  knowledgeTypeTeasersState: KnowledgeTypeTeasersState
}

const getIcon = (knowledgeType: KnowledgeType): ThinIconType => {
  switch (knowledgeType) {
    case 'Products':
      return 'pear-carrot'
    case 'Techniques':
      return 'knife'
    case 'World flavours':
      return 'globe'
    default:
      return 'pan'
  }
}

export const KnowledgeTypeTeasersLayout = (
  p: KnowledgeTypeTeasersLayoutProps
) => {
  return (
    <InView
      as="section"
      threshold={0}
      triggerOnce={true}
      onChange={(isInView, entry) => blockInviewHandler(isInView, entry.target)}
      className="block knowledge-teasers container--m gutter--m"
    >
      <div className="knowledge-teasers__inner">
        {p.knowledgeTypeTeasersState.kind == 'loaded' ? (
          <ul className="knowledge-teasers__list">
            {p.knowledgeTypeTeasersState.value.map((teaser) => {
              const knowledgeType = maybeGetKnowledgeType(teaser.KnowledgeType)
              return (
                knowledgeType && (
                  <li className="knowledge-teasers__item" key={knowledgeType}>
                    <KnowledgeTypeTeaserLayout
                      url={
                        i18next.language == 'en'
                          ? `/en/knowledge/${
                              getKnowledgeTypeSectionOfSlug(knowledgeType).en
                            }`
                          : `/nl/kennis/${
                              getKnowledgeTypeSectionOfSlug(knowledgeType).nl
                            }`
                      }
                      icon={getIcon(knowledgeType)}
                      title={i18next.t(
                        `knowledge:${camelize(knowledgeType)}Title`
                      )}
                      intro={i18next.t(
                        `knowledge:${camelize(knowledgeType)}Intro`
                      )}
                      count={teaser.Count}
                    />
                  </li>
                )
              )
            })}
          </ul>
        ) : p.knowledgeTypeTeasersState.kind === 'loading' ? (
          <RootLayout.Loader hasWrapper={true} showText={true} />
        ) : (
          <RootLayout.LoadingFailed
            text={i18next.t('common:loadingFailed', {
              entity: i18next.t('knowledge:knowledgeTitle').toLowerCase(),
            })}
          />
        )}
      </div>
    </InView>
  )
}
